/**
 * Configuration de l'organisme — Tris Consulting
 * Version simplifiée pour usage personnel.
 */

export interface OrganizationConfig {
  // Informations légales
  name: string;
  legalName: string;
  siret: string;
  siren: string;
  nda: string;
  qualiopi: string;
  qualiopiValidFrom: string;
  qualiopiValidTo: string;
  qualiopiCategories: string[];
  certificationBody: string;
  codeNaf: string;
  urssaf: string;

  // Coordonnées
  address: {
    street: string;
    postalCode: string;
    city: string;
    country: string;
    full: string;
  };
  phone: string;
  email: string;
  website: string;

  // Contact RGPD
  dpo: {
    name: string;
    email: string;
  };

  // Informations bancaires
  bank: {
    name: string;
    iban: string;
    bic: string;
    domiciliation: string;
  };

  // Équipe / Consultants
  team: {
    president: {
      name: string;
      title: string;
    };
    consultants: Array<{
      name: string;
      title: string;
      email: string;
    }>;
  };

  // Consultant par défaut
  defaultConsultant: {
    name: string;
    email: string;
    title: string;
    phone: string;
  };

  // Tarifs
  pricing: {
    test: number;
    essentiel: number;
    approfondi: number;
    strategique: number;
  };
}

/**
 * Configuration Tris Consulting
 */
export const organizationConfig: OrganizationConfig & { qualiopiValidity: string; certificateur: string } = {
  // Informations légales
  name: 'Tris Consulting',
  legalName: 'Tris Consulting',
  siret: '—',
  siren: '—',
  nda: '—',
  qualiopi: '—',
  qualiopiValidFrom: '—',
  qualiopiValidTo: '—',
  qualiopiValidity: '—',
  qualiopiCategories: ['Bilans de compétences'],
  certificationBody: '—',
  certificateur: '—',
  codeNaf: '—',
  urssaf: '—',

  // Coordonnées
  address: {
    street: '—',
    postalCode: '—',
    city: '—',
    country: 'France',
    full: 'France',
  },
  phone: '—',
  email: 'contact@kprecio.com',
  website: 'https://bilan.kprecio.com',

  // Contact RGPD
  dpo: {
    name: 'Tris Consulting',
    email: 'contact@kprecio.com',
  },

  // Informations bancaires
  bank: {
    name: '—',
    iban: '—',
    bic: '—',
    domiciliation: '—',
  },

  // Équipe
  team: {
    president: {
      name: 'Tris',
      title: 'Fondateur',
    },
    consultants: [
      {
        name: 'Tris',
        title: 'Consultant Bilan de Compétences',
        email: 'contact@kprecio.com',
      },
    ],
  },

  // Consultant par défaut pour les documents
  defaultConsultant: {
    name: 'Tris',
    email: 'contact@kprecio.com',
    title: 'Consultant Bilan de Compétences',
    phone: '—',
  },

  // Tarifs TTC
  pricing: {
    test: 0,
    essentiel: 1200,
    approfondi: 1800,
    strategique: 2400,
  },
};

/**
 * Obtenir l'adresse complète formatée
 */
export const getFullAddress = (): string => {
  return organizationConfig.address.full;
};

/**
 * Obtenir les informations légales formatées
 */
export const getLegalInfo = (): string => {
  return `${organizationConfig.legalName} - ${organizationConfig.email}`;
};

/**
 * Obtenir les informations Qualiopi formatées
 */
export const getQualiopiInfo = (): string => {
  return `Bilan de compétences - ${organizationConfig.legalName}`;
};

/**
 * Obtenir le prix d'un forfait
 */
export const getPackagePrice = (packageName: string): number => {
  const name = packageName.toLowerCase();
  if (name.includes('test')) return organizationConfig.pricing.test;
  if (name.includes('essentiel')) return organizationConfig.pricing.essentiel;
  if (name.includes('approfondi')) return organizationConfig.pricing.approfondi;
  if (name.includes('stratégique') || name.includes('strategique')) return organizationConfig.pricing.strategique;
  return organizationConfig.pricing.essentiel;
};

/**
 * Obtenir la durée d'un forfait en heures
 */
export const getPackageDuration = (packageName: string): number => {
  const name = packageName.toLowerCase();
  if (name.includes('test')) return 2;
  if (name.includes('essentiel')) return 12;
  if (name.includes('approfondi')) return 18;
  if (name.includes('stratégique') || name.includes('strategique')) return 24;
  return 12;
};

/**
 * Obtenir les informations bancaires formatées pour les CGV
 */
export const getBankInfo = (): string => {
  const { bank } = organizationConfig;
  return `${bank.name} - IBAN: ${bank.iban} - BIC: ${bank.bic}`;
};

export default organizationConfig;
